<template>
  <div class="deposit-settings">
    <div class="section-header">
      <h3>💰 تنظیمات بیعانه</h3>
      <span :class="['status-badge', form.is_enabled ? 'status-active' : 'status-inactive']">
        {{ form.is_enabled ? 'فعال' : 'غیرفعال' }}
      </span>
    </div>

    <!-- توضیحات -->
    <div class="info-box">
      <p>{{ descriptionText }}</p>
    </div>

    <!-- پیام عدم دسترسی -->
    <div v-if="!bankApproved" class="warning-box">
      ⚠️ برای فعال‌سازی بیعانه، ابتدا باید اطلاعات حساب بانکی شما توسط مدیریت تایید شود.
    </div>

    <form @submit.prevent="submitForm" class="settings-form">
      <!-- ===== فعال/غیرفعال ===== -->
      <div class="form-group">
        <label class="required">وضعیت بیعانه</label>
        <div class="radio-group">
          <label class="radio-label">
            <input
                type="radio"
                v-model="form.is_enabled"
                :value="1"
                :disabled="!bankApproved || isPending"
            />
            فعال
          </label>
          <label class="radio-label">
            <input
                type="radio"
                v-model="form.is_enabled"
                :value="0"
                :disabled="!bankApproved || isPending"
            />
            غیرفعال
          </label>
        </div>
        <small class="help-text" v-if="!bankApproved">
          ⚠️ تا زمانی که حساب بانکی شما تایید نشده، نمی‌توانید بیعانه را فعال کنید.
        </small>
        <small class="help-text" v-else-if="isPending">
          ⏳ اطلاعات بانکی شما در حال بررسی است. پس از تایید می‌توانید تغییرات را اعمال کنید.
        </small>
      </div>

      <!-- ===== انتخاب شرایط اخذ بیعانه ===== -->
      <div class="form-group">
        <label class="required">اخذ بیعانه از</label>
        <div class="radio-group">
          <label class="radio-label">
            <input
                type="radio"
                v-model="form.apply_to"
                value="all"
                :disabled="!bankApproved || isPending || !form.is_enabled"
            />
            همه مشتریان
          </label>
          <label class="radio-label">
            <input
                type="radio"
                v-model="form.apply_to"
                value="new_customers"
                :disabled="!bankApproved || isPending || !form.is_enabled"
            />
            فقط مشتریان جدید
          </label>
          <label class="radio-label">
            <input
                type="radio"
                v-model="form.apply_to"
                value="high_cancel"
                :disabled="!bankApproved || isPending || !form.is_enabled"
            />
            مشتریان با کنسلی بالای ۳۰٪
          </label>
          <label class="radio-label">
            <input
                type="radio"
                v-model="form.apply_to"
                value="both"
                :disabled="!bankApproved || isPending || !form.is_enabled"
            />
            مشتریان جدید + کنسلی بالای ۳۰٪
          </label>
        </div>
        <small class="help-text">
          <span v-if="form.apply_to === 'all'">✅ از همه مشتریان بیعانه دریافت می‌شود.</span>
          <span v-else-if="form.apply_to === 'new_customers'">
            ✅ فقط از مشتریانی که تاکنون از این سالن رزرو نداشته‌اند.
          </span>
          <span v-else-if="form.apply_to === 'high_cancel'">
            ✅ فقط از مشتریانی که نرخ کنسلی آنها بالای ۳۰٪ است.
          </span>
          <span v-else-if="form.apply_to === 'both'">
            ✅ از مشتریان جدید + مشتریان با کنسلی بالای ۳۰٪.
          </span>
        </small>
      </div>

      <!-- ===== مبلغ بیعانه ===== -->
      <div class="form-group">
        <label>کمسیون مبلغ بیعانه</label>
        <div class="deposit-amount-display">
          <span class="amount">2%</span>

        </div>
      </div>

      <!-- ===== دکمه‌ها ===== -->
      <div class="form-actions">
        <button
            type="submit"
            class="btn-primary"
            :disabled="submitting || !bankApproved || isPending"
        >
          {{ submitting ? '⏳ در حال ذخیره...' : '💾 ذخیره تنظیمات' }}
        </button>
        <span v-if="!bankApproved" class="info-text">
          ⏳ منتظر تایید اطلاعات بانکی باشید.
        </span>
        <span v-else-if="isPending" class="info-text">
          ⏳ اطلاعات بانکی در حال بررسی است.
        </span>
        <span v-else-if="form.is_enabled" class="info-text success-text">
          ✅ بیعانه فعال است.
        </span>
        <span v-else class="info-text">
          ⚠️ بیعانه غیرفعال است.
        </span>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DepositSettings',

  data() {
    return {
      loading: false,
      submitting: false,
      bankApproved: false,
      bankStatus: 'pending',
      descriptionText: 'با فعال‌سازی این بخش، از مشتریان بر اساس شرایط انتخابی، مبلغ بیعانه دریافت می‌شود. این مبلغ به عنوان پیش‌پرداخت رزرو محسوب شده و پس از تکمیل رزرو، به حساب شما واریز می‌گردد.',
      form: {
        id: null,
        is_enabled: 0,
        apply_to: 'all'
      },
      depositAmount: 0
    };
  },

  computed: {
    isPending() {
      return this.bankStatus === 'pending';
    },

    formattedDepositAmount() {
      if (!this.depositAmount) return '۰ تومان';
      return new Intl.NumberFormat('fa-IR').format(this.depositAmount) + ' تومان';
    }
  },

  mounted() {
    this.loadData();
  },

  methods: {
    // ==========================================
    // بارگذاری اطلاعات
    // ==========================================
    async loadData() {
      this.loading = true;
      try {
        // ۱. وضعیت بانکی
        const bankRes = await axios.get('/api/admin/bank-account/status',{
          headers:{
            'Authorization':'Bearer ' + localStorage.getItem('token')
          }
        });
        this.bankApproved = bankRes.data.approved || false;
        this.bankStatus = bankRes.data.status || 'pending';

        // ۲. تنظیمات بیعانه
        const depositRes = await axios.get('/api/admin/deposit-settings',
            {
              headers:{
                'Authorization':'Bearer ' + localStorage.getItem('token')
              }
            });
        if (depositRes.data && depositRes.data.id) {
          this.form = {
            id: depositRes.data.id,
            is_enabled: depositRes.data.is_enabled ? 1 : 0,
            apply_to: depositRes.data.apply_to || 'all'
          };
        }

        // ۳. مبلغ بیعانه
        // const amountRes = await axios.get('/api/admin/deposit-amount');
        // this.depositAmount = amountRes.data.amount || 0;

      } catch (error) {
        console.error('خطا در بارگذاری تنظیمات:', error);
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // ارسال فرم
    // ==========================================
    async submitForm() {
      if (!this.bankApproved) {
        alert('ابتدا باید اطلاعات بانکی شما تایید شود.');
        return;
      }

      this.submitting = true;
      try {
        const payload = {
          is_enabled: this.form.is_enabled,
          apply_to: this.form.apply_to
        };

        const url = this.form.id
            ? `/api/admin/deposit-settings/${this.form.id}`
            : '/api/admin/deposit-settings';

        const method = this.form.id ? 'put' : 'post';

        await axios[method](url, payload);

        this.$message?.success('تنظیمات بیعانه با موفقیت ذخیره شد.');
        this.loadData();

      } catch (error) {
        const msg = error.response?.data?.message || 'خطا در ذخیره اطلاعات';
        this.$message?.error(msg);
        console.error(error);
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
/* ========================================== */
/* استایل‌های اصلی */
/* ========================================== */
.deposit-settings {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-top: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.section-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
}

/* ========================================== */
/* بج وضعیت */
/* ========================================== */
.status-badge {
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.status-active {
  background: #f0f9eb;
  color: #67c23a;
}

.status-inactive {
  background: #fef0f0;
  color: #f56c6c;
}

/* ========================================== */
/* باکس‌های اطلاع‌رسانی */
/* ========================================== */
.info-box {
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  color: #2c3e50;
  font-size: 14px;
}

.warning-box {
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  color: #e6a23c;
  font-size: 14px;
}

/* ========================================== */
/* فرم */
/* ========================================== */
.settings-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
}

.form-group label.required::after {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

/* ========================================== */
/* رادیو گروه */
/* ========================================== */
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: normal;
  cursor: pointer;
  padding: 6px 12px;
  background: #f5f7fa;
  border-radius: 6px;
  transition: background 0.2s;
}

.radio-label:hover {
  background: #e8f0fe;
}

.radio-label input[type="radio"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
}

.radio-label input[type="radio"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.help-text {
  color: #909399;
  font-size: 12px;
}

/* ========================================== */
/* نمایش مبلغ بیعانه */
/* ========================================== */
.deposit-amount-display {
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.amount {
  font-size: 18px;
  font-weight: bold;
  color: #409eff;
}

/* ========================================== */
/* دکمه‌ها */
/* ========================================== */
.form-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
}

.btn-primary {
  padding: 10px 28px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover:not(:disabled) {
  background: #66b1ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.btn-primary:disabled {
  background: #a0cfff;
  cursor: not-allowed;
}

.info-text {
  font-size: 14px;
  color: #909399;
}

.success-text {
  color: #67c23a;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .deposit-settings {
    padding: 16px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .radio-group {
    flex-direction: column;
    gap: 8px;
  }

  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }
}
</style>